import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const BLUE = [23, 85, 244];
const INK  = [13, 13, 13];
const MUTED = [136, 136, 136];
const LIGHT = [248, 248, 248];
const BORDER = [232, 232, 232];

function fmtEur(val) {
  if (!val && val !== 0) return '–';
  return Number(val).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function addHeader(doc, title, subtitle) {
  // Black header bar
  doc.setFillColor(...INK);
  doc.rect(0, 0, 210, 22, 'F');

  // IMMOHQ logo text
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('IMMO', 14, 14);
  doc.setTextColor(...BLUE);
  doc.text('HQ', 31, 14);

  // Title on header
  doc.setFontSize(9);
  doc.setTextColor(200, 200, 200);
  doc.setFont('helvetica', 'normal');
  doc.text(title.toUpperCase(), 210 - 14, 14, { align: 'right' });

  // Subtitle below header
  doc.setTextColor(...INK);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(subtitle, 14, 38);

  // Date line
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  doc.text(`Erstellt am ${fmtDate(new Date().toISOString())}`, 14, 46);

  return 55; // y position after header
}

function senderName(sender) {
  if (sender?.company_name) return sender.company_name;
  const name = [sender?.first_name, sender?.last_name].filter(Boolean).join(' ');
  return name || 'ImmoHQ Verwaltung';
}

function addFooter(doc, sender) {
  const pageCount = doc.internal.getNumberOfPages();
  const footerText = sender?.company_name || 'ImmoHQ · Professionelle Immobilienverwaltung';
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.height;
    doc.setFillColor(...BORDER);
    doc.rect(0, pageHeight - 12, 210, 12, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MUTED);
    doc.text(footerText, 14, pageHeight - 4);
    doc.text(`Seite ${i} / ${pageCount}`, 210 - 14, pageHeight - 4, { align: 'right' });
  }
}

function infoRow(doc, y, label, value, bold = false) {
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MUTED);
  doc.text(label, 14, y);

  doc.setFont('helvetica', bold ? 'bold' : 'normal');
  doc.setTextColor(...INK);
  doc.text(String(value || '–'), 80, y);
  return y + 6;
}

// ─── 1. Nebenkostenabrechnung ─────────────────────────────────────────────────
export function generateOperatingCostPDF({ property, year, costs, tenants, sender }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = addHeader(doc, 'Nebenkostenabrechnung', `Betriebskostenabrechnung ${year}`);

  // Property info block
  doc.setFillColor(...LIGHT);
  doc.rect(14, y, 182, 28, 'F');
  doc.setDrawColor(...BORDER);
  doc.rect(14, y, 182, 28, 'S');

  y += 7;
  y = infoRow(doc, y, 'Objekt', property?.name);
  y = infoRow(doc, y, 'Adresse', [property?.address, property?.zip, property?.city].filter(Boolean).join(', '));
  y = infoRow(doc, y, 'Abrechnungszeitraum', `01.01.${year} – 31.12.${year}`);
  y += 4;

  // Summary stats
  const totalCosts = (costs || []).reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const tenantCount = (tenants || []).length;
  const perTenant = tenantCount > 0 ? totalCosts / tenantCount : 0;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text('Übersicht', 14, y);
  y += 6;

  const summaryRows = [
    ['Gesamtkosten', fmtEur(totalCosts), ''],
    ['Mieteranzahl', String(tenantCount), ''],
    ['Umlage pro Mieter', fmtEur(perTenant), 'anteilig'],
  ];

  summaryRows.forEach(([label, value, note]) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(label, 14, y);
    doc.setTextColor(...INK);
    doc.setFont('helvetica', 'bold');
    doc.text(value, 120, y, { align: 'right' });
    if (note) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...MUTED);
      doc.text(note, 140, y);
    }
    y += 6;
  });

  y += 4;

  // Costs table
  const byCategory = {};
  (costs || []).forEach(c => {
    if (!byCategory[c.category]) byCategory[c.category] = 0;
    byCategory[c.category] += Number(c.amount) || 0;
  });

  autoTable(doc, {
    startY: y,
    head: [['Datum', 'Kategorie', 'Notizen', 'Betrag']],
    body: (costs || []).map(c => [
      fmtDate(c.cost_date),
      c.category,
      c.notes || '',
      fmtEur(c.amount),
    ]),
    foot: [['', '', 'Gesamt', fmtEur(totalCosts)]],
    headStyles: { fillColor: INK, textColor: 255, fontSize: 8, fontStyle: 'bold' },
    footStyles: { fillColor: LIGHT, textColor: INK, fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: INK },
    alternateRowStyles: { fillColor: LIGHT },
    columnStyles: { 3: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });

  // Category breakdown
  y = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text('Aufschlüsselung nach Kategorie', 14, y);
  y += 4;

  autoTable(doc, {
    startY: y,
    head: [['Kategorie', 'Betrag', 'Anteil']],
    body: Object.entries(byCategory).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => [
      cat,
      fmtEur(amt),
      totalCosts > 0 ? `${(amt / totalCosts * 100).toFixed(1)}%` : '–',
    ]),
    headStyles: { fillColor: INK, textColor: 255, fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: INK },
    alternateRowStyles: { fillColor: LIGHT },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
    margin: { left: 14, right: 14 },
  });

  addFooter(doc, sender);
  doc.save(`Nebenkostenabrechnung_${property?.name || 'Objekt'}_${year}.pdf`);
}

// ─── 2. Mahnbrief ─────────────────────────────────────────────────────────────
export function generateReminderPDF({ tenant, property, payment, level, dueDate, fee, sender }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const LEVEL_LABEL = {
    1: 'Zahlungserinnerung',
    2: '1. Mahnung',
    3: '2. Mahnung (Letzte Mahnung)',
  };

  let y = addHeader(doc, LEVEL_LABEL[level] || 'Mahnung', LEVEL_LABEL[level] || 'Mahnung');

  // Recipient block
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text(`${tenant?.first_name} ${tenant?.last_name}`, 14, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  if (property?.address) { doc.text(property.address, 14, y); y += 5; }
  if (property?.zip || property?.city) { doc.text(`${property?.zip || ''} ${property?.city || ''}`.trim(), 14, y); y += 5; }
  y += 8;

  // Date
  doc.setTextColor(...MUTED);
  doc.setFontSize(8);
  doc.text(fmtDate(new Date().toISOString()), 210 - 14, y - 8, { align: 'right' });

  // Salutation
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  doc.text(`Sehr geehrte/r ${tenant?.first_name} ${tenant?.last_name},`, 14, y);
  y += 7;

  const outstanding = (Number(payment?.amount_due) || 0) - (Number(payment?.amount_paid) || 0);
  const totalDue    = outstanding + (fee || 0);

  // Body text
  const bodyText = level === 1
    ? `wir möchten Sie freundlich daran erinnern, dass die Mietzahlung für ${fmtDate(payment?.due_date)} noch aussteht. Bitte überweisen Sie den ausstehenden Betrag umgehend.`
    : `wir haben festgestellt, dass die Mietzahlung für ${fmtDate(payment?.due_date)} trotz unserer vorherigen Erinnerung noch nicht eingegangen ist. Wir fordern Sie auf, den Rückstand unverzüglich zu begleichen.`;

  const lines = doc.splitTextToSize(bodyText, 182);
  doc.setFontSize(9);
  doc.text(lines, 14, y);
  y += lines.length * 5 + 6;

  // Payment details box
  doc.setFillColor(...LIGHT);
  doc.rect(14, y, 182, level > 1 ? 30 : 24, 'F');
  doc.setDrawColor(...INK);
  doc.rect(14, y, 182, level > 1 ? 30 : 24, 'S');
  y += 8;

  y = infoRow(doc, y, 'Ausstehende Miete', fmtEur(outstanding));
  if (fee) y = infoRow(doc, y, `Mahngebühr (Stufe ${level})`, fmtEur(fee));
  y = infoRow(doc, y, 'Gesamtbetrag', fmtEur(totalDue), true);
  y += 6;

  y = infoRow(doc, y, 'Zahlbar bis', fmtDate(dueDate));
  y += 10;

  // Closing
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  const closing = level < 3
    ? 'Sollte die Zahlung bereits erfolgt sein, betrachten Sie dieses Schreiben bitte als gegenstandslos. Bei Fragen stehen wir Ihnen gerne zur Verfügung.'
    : 'Sollte die Zahlung nicht bis zum oben genannten Datum eingehen, behalten wir uns vor, rechtliche Schritte einzuleiten.';

  const closingLines = doc.splitTextToSize(closing, 182);
  doc.text(closingLines, 14, y);
  y += closingLines.length * 5 + 10;

  doc.text('Mit freundlichen Grüßen', 14, y);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.text(senderName(sender), 14, y);
  if (sender?.address) {
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(sender.address, 14, y);
  }

  addFooter(doc, sender);
  doc.save(`Mahnung_${tenant?.last_name}_${LEVEL_LABEL[level]?.replace(/\s+/g, '_')}.pdf`);
}

// ─── 3. Übergabeprotokoll ─────────────────────────────────────────────────────
export function generateHandoverPDF({ property, unit, tenant, landlord, handoverDate, type = 'einzug', notes = '', rooms = [] }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const typeLabel = type === 'einzug' ? 'Einzugsprotokoll' : 'Auszugsprotokoll';
  let y = addHeader(doc, typeLabel, typeLabel);

  // Parties + property
  doc.setFillColor(...LIGHT);
  doc.rect(14, y, 182, 38, 'F');
  doc.setDrawColor(...BORDER);
  doc.rect(14, y, 182, 38, 'S');
  y += 7;
  y = infoRow(doc, y, 'Objekt', property?.name);
  y = infoRow(doc, y, 'Adresse', [property?.address, property?.zip, property?.city].filter(Boolean).join(', '));
  y = infoRow(doc, y, 'Einheit', unit || tenant?.unit || '–');
  y = infoRow(doc, y, 'Übergabedatum', fmtDate(handoverDate || new Date().toISOString()));
  y += 6;

  // Mieter / Vermieter
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
  doc.text('Mieter', 14, y); y += 4;
  doc.setFillColor(...LIGHT); doc.rect(14, y, 87, 20, 'F');
  doc.setDrawColor(...BORDER); doc.rect(14, y, 87, 20, 'S');
  y += 6;
  y = infoRow(doc, y, 'Name', `${tenant?.first_name || ''} ${tenant?.last_name || ''}`.trim());
  y = infoRow(doc, y, 'E-Mail', tenant?.email);
  y += 8;

  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
  doc.text('Vermieter', 109, y - 20 + 6 - 4); // position next to mieter block
  doc.setFillColor(...LIGHT); doc.rect(109, y - 20, 87, 20, 'F');
  doc.setDrawColor(...BORDER); doc.rect(109, y - 20, 87, 20, 'S');
  const vName = landlord?.company_name || [landlord?.first_name, landlord?.last_name].filter(Boolean).join(' ') || 'ImmoHQ';
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...MUTED);
  doc.text('Name', 113, y - 20 + 7);
  doc.setTextColor(...INK); doc.text(vName, 140, y - 20 + 7);

  // Rooms table
  if (rooms.length > 0) {
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
    doc.text('Raumzustand', 14, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [['Raum', 'Zustand', 'Bemerkungen']],
      body: rooms.map(r => [r.name, r.condition || '–', r.notes || '']),
      headStyles: { fillColor: INK, textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: INK },
      alternateRowStyles: { fillColor: LIGHT },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 10;
  } else {
    // Empty rooms table template
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
    doc.text('Raumzustand', 14, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [['Raum', 'Zustand', 'Zählerstände / Bemerkungen']],
      body: [
        ['Eingang', '', ''],
        ['Wohnzimmer', '', ''],
        ['Schlafzimmer', '', ''],
        ['Küche', '', ''],
        ['Bad / WC', '', ''],
        ['Keller / Stellplatz', '', ''],
      ],
      headStyles: { fillColor: INK, textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: INK, minCellHeight: 10 },
      alternateRowStyles: { fillColor: LIGHT },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // Notes
  if (notes) {
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
    doc.text('Anmerkungen', 14, y); y += 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    const noteLines = doc.splitTextToSize(notes, 182);
    doc.text(noteLines, 14, y);
    y += noteLines.length * 5 + 8;
  }

  // Signature fields
  if (y > 230) doc.addPage();
  y = Math.max(y, 220);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...MUTED);
  doc.text('Die Übergabe wurde durch beide Parteien bestätigt.', 14, y); y += 12;

  doc.setDrawColor(...INK);
  doc.line(14, y, 80, y); doc.line(120, y, 196, y); y += 5;
  doc.text(vName, 14, y);
  doc.text(`${tenant?.first_name || ''} ${tenant?.last_name || ''}`.trim(), 120, y);

  addFooter(doc, landlord);
  doc.save(`${typeLabel}_${tenant?.last_name || 'Mieter'}_${fmtDate(handoverDate || new Date().toISOString()).replace(/\./g, '-')}.pdf`);
}

// ─── 4. Mietvertrag ──────────────────────────────────────────────────────────
export function generateMietvertragPDF({ tenant, property, sender, leaseStart, leaseEnd, monthlyRent, deposit, specialClauses = '' }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = addHeader(doc, 'Mietvertrag', 'Mietvertrag');

  // Vermieter block
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
  doc.text('§ 1 Vermieter', 14, y); y += 5;
  doc.setFillColor(...LIGHT); doc.rect(14, y, 182, 20, 'F');
  doc.setDrawColor(...BORDER); doc.rect(14, y, 182, 20, 'S');
  y += 6;
  y = infoRow(doc, y, 'Name / Firma', senderName(sender));
  y = infoRow(doc, y, 'Adresse', sender?.address || '–');
  y += 4;

  // Mieter block
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
  doc.text('§ 2 Mieter', 14, y); y += 5;
  doc.setFillColor(...LIGHT); doc.rect(14, y, 182, 20, 'F');
  doc.setDrawColor(...BORDER); doc.rect(14, y, 182, 20, 'S');
  y += 6;
  y = infoRow(doc, y, 'Name', `${tenant?.first_name || ''} ${tenant?.last_name || ''}`.trim());
  y = infoRow(doc, y, 'E-Mail', tenant?.email || '–');
  y += 4;

  // Mietobjekt
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
  doc.text('§ 3 Mietobjekt', 14, y); y += 5;
  doc.setFillColor(...LIGHT); doc.rect(14, y, 182, 25, 'F');
  doc.setDrawColor(...BORDER); doc.rect(14, y, 182, 25, 'S');
  y += 6;
  y = infoRow(doc, y, 'Objekt', property?.name);
  y = infoRow(doc, y, 'Adresse', [property?.address, property?.zip, property?.city].filter(Boolean).join(', '));
  y = infoRow(doc, y, 'Einheit', tenant?.unit || '–');
  y += 4;

  // Mietzeit & Miete
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
  doc.text('§ 4 Mietzeit und Miete', 14, y); y += 5;
  doc.setFillColor(...LIGHT); doc.rect(14, y, 182, 28, 'F');
  doc.setDrawColor(...BORDER); doc.rect(14, y, 182, 28, 'S');
  y += 6;
  y = infoRow(doc, y, 'Mietbeginn', fmtDate(leaseStart));
  y = infoRow(doc, y, 'Mietende', leaseEnd ? fmtDate(leaseEnd) : 'Unbefristet');
  y = infoRow(doc, y, 'Kaltmiete / Monat', fmtEur(monthlyRent), true);
  y = infoRow(doc, y, 'Kaution', deposit ? fmtEur(deposit) : '–');
  y += 6;

  // Standard clauses
  const clauses = [
    { title: '§ 5 Nebenkosten', text: 'Die Nebenkosten werden nach einem gesonderten Nebenkostenspiegel monatlich als Vorauszahlung geleistet. Eine jährliche Abrechnung erfolgt bis zum 31.12. des Folgejahres.' },
    { title: '§ 6 Kaution', text: `Die Mietsicherheit beträgt ${deposit ? fmtEur(deposit) : 'gemäß Vereinbarung'}. Sie ist zu Beginn des Mietverhältnisses fällig.` },
    { title: '§ 7 Tierhaltung', text: 'Die Haltung von Haustieren bedarf der vorherigen schriftlichen Zustimmung des Vermieters.' },
    { title: '§ 8 Schönheitsreparaturen', text: 'Schönheitsreparaturen während der Mietzeit obliegen dem Mieter im Rahmen des üblichen Nutzungsverschleißes.' },
    { title: '§ 9 Untervermietung', text: 'Eine Untervermietung der Mietsache oder von Teilen ist nur mit schriftlicher Genehmigung des Vermieters erlaubt.' },
  ];

  for (const clause of clauses) {
    if (y > 250) { doc.addPage(); y = 20; }
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
    doc.text(clause.title, 14, y); y += 5;
    const lines = doc.splitTextToSize(clause.text, 182);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...INK);
    doc.text(lines, 14, y);
    y += lines.length * 4.5 + 6;
  }

  // Special clauses
  if (specialClauses) {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...INK);
    doc.text('§ 10 Besondere Vereinbarungen', 14, y); y += 5;
    const scLines = doc.splitTextToSize(specialClauses, 182);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text(scLines, 14, y);
    y += scLines.length * 4.5 + 6;
  }

  // Signatures
  if (y > 230) { doc.addPage(); y = 20; }
  y = Math.max(y + 10, 220);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...MUTED);
  doc.text(`Ort, Datum: ________________________________`, 14, y); y += 14;
  doc.setDrawColor(...INK);
  doc.line(14, y, 80, y); doc.line(120, y, 196, y); y += 5;
  doc.text(senderName(sender) + ' (Vermieter)', 14, y);
  doc.text(`${tenant?.first_name || ''} ${tenant?.last_name || ''} (Mieter)`, 120, y);

  addFooter(doc, sender);
  doc.save(`Mietvertrag_${tenant?.last_name || 'Mieter'}_${fmtDate(leaseStart).replace(/\./g, '-')}.pdf`);
}

// ─── 5. Mietbescheinigung ─────────────────────────────────────────────────────
export function generateTenantCertificatePDF({ tenant, property, sender }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = addHeader(doc, 'Mietbescheinigung', 'Mietbescheinigung');

  // Intro
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...INK);
  const intro = `Hiermit wird bestätigt, dass die nachfolgend genannte Person Mieter/in der aufgeführten Immobilie ist.`;
  const introLines = doc.splitTextToSize(intro, 182);
  doc.text(introLines, 14, y);
  y += introLines.length * 5 + 10;

  // Tenant block
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text('Mieterdaten', 14, y);
  y += 4;

  doc.setFillColor(...LIGHT);
  doc.rect(14, y, 182, 40, 'F');
  doc.setDrawColor(...BORDER);
  doc.rect(14, y, 182, 40, 'S');
  y += 7;

  y = infoRow(doc, y, 'Name', `${tenant?.last_name}, ${tenant?.first_name}`);
  y = infoRow(doc, y, 'E-Mail', tenant?.email);
  y = infoRow(doc, y, 'Telefon', tenant?.phone);
  y = infoRow(doc, y, 'IBAN', tenant?.iban);
  y += 6;

  // Property block
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text('Mietobjekt', 14, y);
  y += 4;

  doc.setFillColor(...LIGHT);
  doc.rect(14, y, 182, 40, 'F');
  doc.setDrawColor(...BORDER);
  doc.rect(14, y, 182, 40, 'S');
  y += 7;

  y = infoRow(doc, y, 'Objekt', property?.name);
  y = infoRow(doc, y, 'Adresse', property?.address);
  y = infoRow(doc, y, 'PLZ / Stadt', `${property?.zip || ''} ${property?.city || ''}`.trim());
  y = infoRow(doc, y, 'Einheit', tenant?.unit);
  y += 6;

  // Lease block
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text('Mietvertrag', 14, y);
  y += 4;

  doc.setFillColor(...LIGHT);
  doc.rect(14, y, 182, 30, 'F');
  doc.setDrawColor(...BORDER);
  doc.rect(14, y, 182, 30, 'S');
  y += 7;

  y = infoRow(doc, y, 'Mietbeginn', fmtDate(tenant?.lease_start));
  y = infoRow(doc, y, 'Mietende', tenant?.lease_end ? fmtDate(tenant.lease_end) : 'Unbefristet');
  y = infoRow(doc, y, 'Monatsmiete', fmtEur(tenant?.monthly_rent), true);
  y += 10;

  // Signature
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...INK);
  doc.text(`Diese Bescheinigung wurde am ${fmtDate(new Date().toISOString())} ausgestellt.`, 14, y);
  y += 20;

  doc.setDrawColor(...INK);
  doc.line(14, y, 80, y);
  doc.line(120, y, 196, y);
  y += 5;
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(senderName(sender), 14, y);
  doc.text('Datum / Unterschrift Mieter', 120, y);

  addFooter(doc, sender);
  doc.save(`Mietbescheinigung_${tenant?.last_name}_${tenant?.first_name}.pdf`);
}
